import { useEffect, useState } from 'react';
import axios from 'axios';
import { CircleSlash, RefreshCw, ScanSearch, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtVES = (n) => `VES ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

// Cobros DI donde el banco respondió 0000 pero el extracto no trae un crédito con
// la referencia del cobro. El verificador se detiene aquí a propósito: no acredita
// ni revierte. Lo clave es `claimed_by`: si el crédito candidato ya lo cobró otra
// orden, acreditarlo duplicaría ese dinero y el boton queda bloqueado.
export default function DIReviewQueue({ setToast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [action, setAction] = useState(null); // {type:'approve'|'discard', item}
  const [confirmAmount, setConfirmAmount] = useState('');
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      const res = await axios.get(`${API}/di-review`, { headers: headers() });
      setData(res.data);
    } catch (e) {
      setData(null);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    const { type, item } = action;
    setBusy(item.order_id);
    try {
      if (type === 'approve') {
        await axios.post(`${API}/di-review/${item.order_id}/approve`,
          { confirm_amount: parseFloat(confirmAmount) }, { headers: headers() });
        setToast?.({ type: 'success', message: `${item.order_code} acreditada por ${fmtVES(item.amount)}` });
      } else {
        await axios.post(`${API}/di-review/${item.order_id}/discard`,
          { note }, { headers: headers() });
        setToast?.({ type: 'success', message: `${item.order_code} descartada` });
      }
      setAction(null); setConfirmAmount(''); setNote('');
      await load();
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'No se pudo completar' });
    } finally {
      setBusy(null);
    }
  };

  if (!data || data.total_count === 0) return null;

  return (
    <div className="mb-6 rounded-xl border border-orange-300 bg-orange-50 p-5" data-testid="di-review-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <ScanSearch className="w-6 h-6 text-orange-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-bold text-orange-900" data-testid="di-review-title">
              Cobros DI en revisión ({data.total_count}) — {fmtVES(data.total_amount)}
            </h3>
            <p className="text-xs text-orange-800 mt-1 max-w-3xl">
              El banco respondió 0000 pero en el extracto no hay un crédito con la referencia del
              cobro. No se acreditó ni se revirtió nada: lo decides tú.
              {data.blocked_count > 0 && (
                <> <strong>{data.blocked_count} de estos casos tienen el crédito ya cobrado por otra
                orden</strong>, así que acreditarlos duplicaría ese dinero y están bloqueados.</>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          data-testid="di-review-refresh"
          className="text-xs px-3 py-1.5 border rounded-lg bg-white hover:bg-gray-50 flex items-center gap-1"
        ><RefreshCw className="w-3 h-3" /> Revisar</button>
      </div>

      <div className="mt-4 space-y-3">
        {data.items.map((it) => (
          <div key={it.order_id} className="bg-white rounded-lg border p-3" data-testid={`di-review-row-${it.order_code}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="text-xs space-y-0.5">
                <p className="font-bold text-sm text-gray-900">
                  {it.order_code} · {fmtVES(it.amount)}
                  <span className="ml-2 font-normal text-gray-500">{it.company_name}</span>
                </p>
                <p className="text-gray-600">
                  Cobrada {fmtDate(it.created_at)} · orden <span className="font-medium">{it.order_status}</span>
                  {it.payer_name ? ` · pagador ${it.payer_name}` : ''}
                </p>
                <p className="font-mono text-[11px] text-gray-500">endtoend {it.endtoend || '—'}</p>
                {it.candidate_credit && (
                  <p className="text-gray-700">
                    Candidato del extracto: ref <span className="font-mono">{it.candidate_credit.referencia}</span>
                    {' '}· {it.candidate_credit.fecha} · {fmtVES(it.candidate_credit.monto)}
                  </p>
                )}
                {it.claimed_by ? (
                  <p className="text-red-700 font-medium flex items-center gap-1">
                    <ShieldAlert className="w-3.5 h-3.5" />
                    Ese crédito ya lo cobró la orden {it.claimed_by.order_code} ({it.claimed_by.status}, {fmtDate(it.claimed_by.paid_at)})
                  </p>
                ) : (
                  <p className="text-emerald-700 font-medium">Ninguna otra orden reclama ese crédito</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  disabled={!it.can_approve || busy === it.order_id}
                  onClick={() => { setAction({ type: 'approve', item: it }); setConfirmAmount(''); }}
                  data-testid={`di-review-approve-${it.order_code}`}
                  title={it.can_approve ? 'Acreditar a mano' : 'Bloqueado: el crédito ya lo cobró otra orden'}
                  className="px-3 py-1.5 text-xs rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-30 disabled:cursor-not-allowed"
                >Acreditar</button>
                <button
                  disabled={busy === it.order_id}
                  onClick={() => { setAction({ type: 'discard', item: it }); setNote(it.claimed_by ? 'Crédito pertenece a otra orden' : ''); }}
                  data-testid={`di-review-discard-${it.order_code}`}
                  className="px-3 py-1.5 text-xs rounded-lg border bg-white hover:bg-gray-50 flex items-center gap-1"
                ><CircleSlash className="w-3 h-3" /> Descartar</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {action && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAction(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="di-review-modal">
            <h4 className="font-bold mb-2">
              {action.type === 'approve' ? 'Acreditar cobro a mano' : 'Descartar de la bandeja'}
            </h4>
            <p className="text-xs text-gray-500 mb-4">
              Orden {action.item.order_code} · {fmtVES(action.item.amount)} · {action.item.company_name}
            </p>
            {action.type === 'approve' ? (
              <>
                <p className="text-xs text-gray-600 mb-2">
                  Esto acredita el dinero al ledger del comercio y dispara el webhook de pago.
                  Escribe el monto exacto para confirmar.
                </p>
                <input
                  type="number" step="0.01" value={confirmAmount}
                  data-testid="di-review-confirm-amount"
                  onChange={(e) => setConfirmAmount(e.target.value)}
                  placeholder={action.item.amount.toFixed(2)}
                  className="w-full px-3 py-2 border rounded-lg mb-4"
                />
              </>
            ) : (
              <>
                <p className="text-xs text-gray-600 mb-2">
                  No se acredita ni se revierte nada: solo sale de la bandeja con tu nota.
                </p>
                <input
                  type="text" value={note}
                  data-testid="di-review-note"
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Motivo"
                  className="w-full px-3 py-2 border rounded-lg mb-4"
                />
              </>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setAction(null)} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
              <button
                onClick={submit}
                data-testid="di-review-modal-confirm"
                className={`px-4 py-2 text-sm text-white rounded-lg ${action.type === 'approve' ? 'bg-emerald-700' : 'bg-gray-800'}`}
              >{action.type === 'approve' ? 'Acreditar' : 'Descartar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
